/**
 * Use horizontal medium article row when there is a need to promote 1-2 stories or promotions of lesser importance, and when you want the image and text to be next to each other.
 *
 * @module views/components/ArticleRowHorizontalMedium
 * @memberof -Common
 */
import './ArticleRowHorizontalMedium.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import { constants } from '@ulta/utils/constants/constants';

import Article from '../Article/Article';
import { upCount } from '../ArticleRowStackedCompact/ArticleRowStackedCompact';

/**
 * Represents a ArticleRowHorizontalMedium component
 *
 * @method
 * @param {ArticleRowHorizontalMediumProps} props - React properties passed from composition
 * @returns ArticleRowHorizontalMedium
 */
export const ArticleRowHorizontalMedium = function( props ){
  const {
    backgroundColor,
    patternBackground,
    article,
    title,
    eyebrow,
    subtitle,
    textLayout,
    cardColor,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;
  const upCountText = upCount( article?.length );
  const articleRowHorMedRef = useRef( null );
  useIntersectionObserver( articleRowHorMedRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  if( !article ){
    return null;
  }

  return (
    <div className={
      classNames( 'ArticleRowHorizontalMedium', {
        'ArticleRowHorizontalMedium__withBackgroundColor': backgroundColor && !patternBackground && !cardColor
      } )
    }
    ref={ articleRowHorMedRef }
    >
      { patternBackground &&
        <div className='ArticleRowHorizontalMedium__patternBackground'>
          <UltaAsset
            { ...spreadProps( {
              ...patternBackground
            } ) }
          />
        </div>
      }
      <GridContainer
        { ...spreadProps( {
          fullBleedContentBackgroundColor: ( !patternBackground && !cardColor ) ? backgroundColor : undefined
        } ) }
      >
        { eyebrow &&
          <div className={ classNames( 'ArticleRowHorizontalMedium__headingEyebrow', {
            'ArticleRowHorizontalMedium__headingEyebrow--withPatternBgPosition': patternBackground
          } )
          }
          >
            <Text
              htmlTag='p'
              textStyle='eyebrow'
              textAlign='center'
            >
              { eyebrow }
            </Text>
          </div>
        }
        { title?.text &&
          <div className={ classNames( 'ArticleRowHorizontalMedium__headingTitle', {
            'ArticleRowHorizontalMedium__headingTitle--withPatternBgPosition': patternBackground && !eyebrow
          } )
          }
          >
            <Text
              htmlTag={ title.htmlTag }
              textStyle='title-4'
              textAlign='center'
            >
              { title.text }
            </Text>
          </div>
        }
        { subtitle &&
          <div className='ArticleRowHorizontalMedium__headingSubtitle'>
            <Text htmlTag='p'
              textStyle='subtitle-1'
              textAlign='center'
            >
              { subtitle }
            </Text>
          </div>
        }
        { article &&
          article.map( ( content, index ) => (
            <Article
              { ...spreadProps( { ...content } ) }
              cardOrientation='horizontal'
              articleTextAlign='left'
              textLayout={ cardColor ? 'centered' : textLayout }
              cardColor={ cardColor }
              backgroundColor={ backgroundColor }
              upCountText={ upCountText }
              upCountCounter={ index }
              key={ index }
            />
          ) )
        }
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ArticleRowHorizontalMediumProps
 * @type {object}
 * @property {object} title - Sets the title properties
 * @property {string} eyebrow - Sets the eyebrow
 * @property {string} subtitle - Sets the subtitle
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} patternBackground - Sets the patternBackground
 * @property {object} article - Sets the article properties
 * @property {string} textLayout - Sets the layout of the card text
 * @property {string} cardColor - Sets the background color of the card
 * @property {object} linkAction - Sets the linkAction
 */

export const propTypes = {
  title: PropTypes.object,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.string,
  backgroundColor: PropTypes.string,
  patternBackground:  PropTypes.object,
  textLayout: PropTypes.string,
  cardColor: PropTypes.string,
  article: PropTypes.array,
  linkAction: PropTypes.object
};

/**
* Default values for passed properties
* @type object
*/
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ArticleRowHorizontalMedium.defaultProps = defaultProps;
ArticleRowHorizontalMedium.propTypes = propTypes;

export default ArticleRowHorizontalMedium;
